import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import ResourcePage, { PageHeader } from "../components/ResourcePage";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Baby, Skull, ShoppingBag, PackagePlus, Activity, Syringe, Wheat, Scale,
} from "lucide-react";
import { toast } from "sonner";

const SPECIES = ["Pigs", "Snails", "Fish", "Goats", "Poultry"].map((s) => ({ value: s, label: s }));

function Stat({ icon: Icon, label, value, tone = "green" }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700", red: "bg-red-50 text-red-600",
    blue: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700",
  };
  return (
    <Card className="p-4" data-testid={`ls-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-[18px] w-[18px]" /></div>
      <p className="num font-display text-2xl font-extrabold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </Card>
  );
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/livestock/analytics").then(({ data }) => setD(data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!d) return <p className="py-10 text-center text-muted-foreground">Loading analytics…</p>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Baby} label="Births" value={d.births} tone="green" />
        <Stat icon={Skull} label="Deaths" value={d.deaths} tone="red" />
        <Stat icon={ShoppingBag} label="Sold" value={d.sales} tone="blue" />
        <Stat icon={PackagePlus} label="Purchased" value={d.purchases} tone="amber" />
        <Stat icon={Activity} label="Current Head" value={d.current_head} tone="green" />
        <Stat icon={Skull} label="Mortality Rate" value={`${d.mortality_rate}%`} tone="red" />
        <Stat icon={Wheat} label="Feed Cost" value={fmtMoney(d.feed_cost)} tone="amber" />
        <Stat icon={Syringe} label="Health Cost" value={fmtMoney(d.health_cost)} tone="blue" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-bold tracking-tight">Upcoming Vaccinations</h3>
          {d.upcoming_vaccinations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No scheduled vaccinations. Add a vaccination event with a future date.</p>
          ) : (
            <div className="space-y-3">
              {d.upcoming_vaccinations.map((v, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                  <div>
                    <p className="text-sm font-semibold">{v.item || "Vaccination"} · {v.species}</p>
                    <p className="text-xs text-muted-foreground">{v.tag || "All"} </p>
                  </div>
                  <Badge className="border-0 bg-sky-100 text-sky-700">{fmtDate(v.date)}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-bold tracking-tight">Live Head by Species</h3>
          {d.species_head.length === 0 ? (
            <p className="text-sm text-muted-foreground">No livestock recorded yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {d.species_head.map((s) => (
                <div key={s.name} className="rounded-lg border border-border bg-secondary/40 p-4">
                  <p className="num font-display text-2xl font-extrabold text-primary">{s.value}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.name}</p>
                </div>
              ))}
            </div>
          )}
          {d.weight_trend.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Scale className="h-3.5 w-3.5" />Avg Weight Trend (kg)</p>
              <div className="flex items-end gap-2">
                {d.weight_trend.map((w) => (
                  <div key={w.month} className="flex flex-1 flex-col items-center">
                    <div className="num text-xs font-semibold">{w.avg_weight}</div>
                    <div className="w-full rounded-t bg-primary/70" style={{ height: `${Math.min(80, w.avg_weight)}px` }} />
                    <div className="mt-1 text-[10px] text-muted-foreground">{w.month.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export function LivestockHub() {
  return (
    <div className="fade-up">
      <PageHeader title="Livestock" subtitle="Animals, health & breeding records, and herd analytics." />
      <Tabs defaultValue="animals">
        <TabsList className="mb-5">
          <TabsTrigger value="animals" data-testid="ls-tab-animals">Animals</TabsTrigger>
          <TabsTrigger value="health" data-testid="ls-tab-health">Health & Events</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="ls-tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="animals">
          <ResourcePage
            embedded
            title="Livestock"
            subtitle="Herds, flocks and individual animals with valuation."
            endpoint="livestock"
            testid="ls"
            columns={[
              { key: "tag", label: "Tag / ID", bold: true },
              { key: "species", label: "Species" },
              { key: "breed", label: "Breed" },
              { key: "count", label: "Head", type: "number" },
              { key: "status", label: "Status", type: "badge" },
              { key: "weight", label: "Weight (kg)", type: "number" },
              { key: "value_per_head", label: "Value/head", type: "money" },
            ]}
            fields={[
              { key: "tag", label: "Tag / ID", type: "text" },
              { key: "species", label: "Species", type: "select", options: SPECIES },
              { key: "breed", label: "Breed", type: "text" },
              { key: "count", label: "Head count", type: "number", default: 1 },
              { key: "status", label: "Status", type: "select", options: [
                { value: "healthy", label: "Healthy" }, { value: "sick", label: "Sick" },
                { value: "pregnant", label: "Pregnant" }, { value: "sold", label: "Sold" },
                { value: "deceased", label: "Deceased" }] },
              { key: "weight", label: "Avg weight (kg)", type: "number" },
              { key: "value_per_head", label: "Estimated value per head (₦)", type: "number" },
              { key: "acquired_date", label: "Acquired date", type: "date" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]}
          />
        </TabsContent>

        <TabsContent value="health">
          <ResourcePage
            embedded
            title="Health & Events"
            subtitle="Log births, deaths, sales, purchases, weights, vaccinations, medication and feed."
            endpoint="livestock-events"
            testid="lse"
            columns={[
              { key: "date", label: "Date", type: "date" },
              { key: "event_type", label: "Event", type: "badge" },
              { key: "species", label: "Species" },
              { key: "tag", label: "Tag" },
              { key: "count", label: "Count", type: "number" },
              { key: "weight", label: "Weight", type: "number" },
              { key: "item", label: "Item" },
              { key: "cost", label: "Cost", type: "money" },
            ]}
            fields={[
              { key: "event_type", label: "Event type", type: "select", options: [
                { value: "birth", label: "Birth" }, { value: "death", label: "Death" },
                { value: "sale", label: "Sale" }, { value: "purchase", label: "Purchase" },
                { value: "weight", label: "Weight log" }, { value: "vaccination", label: "Vaccination" },
                { value: "medication", label: "Medication" }, { value: "feed", label: "Feed" }] },
              { key: "species", label: "Species", type: "select", options: SPECIES },
              { key: "tag", label: "Animal tag (optional)", type: "text" },
              { key: "date", label: "Date", type: "date" },
              { key: "count", label: "Count (births/deaths/sales)", type: "number" },
              { key: "weight", label: "Weight kg (weight logs)", type: "number" },
              { key: "item", label: "Vaccine / medication / feed name", type: "text" },
              { key: "cost", label: "Cost (₦)", type: "number" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]}
          />
        </TabsContent>

        <TabsContent value="analytics"><Analytics /></TabsContent>
      </Tabs>
    </div>
  );
}
